import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Upload, Camera, X, CheckCircle2, Loader2, Lock, Share2, FileText, AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SignaturePad from '../components/SignaturePad';
import { adminAxios, useAuth, formatApiErrorDetail } from '../lib/AuthContext';

const DEFAULT_CHECKLIST = [
  'Filters cleaned / replaced',
  'Indoor coil inspected',
  'Outdoor coil cleaned',
  'Refrigerant pressures within spec',
  'Condensate drain flow tested',
  'Electrical isolator & connections checked',
  'Fan motor operation verified',
  'Thermostat / controls calibrated',
  'Air-flow & temperature differential measured',
  'Customer demo & operational handover',
];

const STATUS_LABELS = [
  { key: 'pass', label: 'Pass', color: 'bg-emerald-500 text-white border-emerald-500' },
  { key: 'fail', label: 'Fail', color: 'bg-red-500 text-white border-red-500' },
  { key: 'n/a', label: 'N/A', color: 'bg-slate-600 text-white border-slate-500' },
];

const MAX_PHOTOS = 9;

function freshSheet(user, company) {
  return {
    engineer: {
      name: user?.name || '',
      email: user?.email || '',
      phone: company?.phone || '02080599941',
    },
    client: { name: '', address: '', contact: '', propertyType: 'domestic' },
    equipment: { type: '', model: '', serial: '', location: '', refrigerant: '' },
    checklist: DEFAULT_CHECKLIST.map((label) => ({ label, status: 'n/a', notes: '' })),
    technicalMetrics: [emptyMetric()],
    remedial: { faultsFound: '', repairsRecommended: '', quoteRequired: false },
    notes: '',
    serviceDate: new Date().toISOString(),
  };
}

function emptyMetric() {
  return { unitId: '', location: '', gasType: '', charge: '', returnAir: '', supplyAir: '', leakStatus: 'N/A' };
}

function SectionCard({ title, children, testId }) {
  return (
    <section
      className="bg-[#1e2d4a]/40 border border-white/10 rounded-2xl p-5 sm:p-6"
      data-testid={testId}
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-300 mb-4">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, children, htmlFor }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wider text-slate-400">
        {label}
      </Label>
      {children}
    </div>
  );
}

export default function AdminServiceSheet() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const engineerSigRef = useRef(null);
  const clientSigRef = useRef(null);

  const [sheet, setSheet] = useState(() => freshSheet(user));
  const [sheetId, setSheetId] = useState(routeId && routeId !== 'new' ? routeId : null);
  const [serverDoc, setServerDoc] = useState(null);
  const [loading, setLoading] = useState(routeId && routeId !== 'new');
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isFinalized = serverDoc?.status === 'finalized';

  // Load existing sheet
  useEffect(() => {
    if (!sheetId) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await adminAxios.get(`/service-sheets/${sheetId}`);
        if (cancel) return;
        setServerDoc(data);
        setSheet({
          engineer: data.engineer || freshSheet(user).engineer,
          client: data.client || { name: '', address: '', contact: '', propertyType: 'domestic' },
          equipment: data.equipment || { type: '', model: '', serial: '', location: '', refrigerant: '' },
          checklist:
            (data.checklist && data.checklist.length
              ? data.checklist
              : freshSheet(user).checklist),
          technicalMetrics:
            (data.technicalMetrics && data.technicalMetrics.length
              ? data.technicalMetrics
              : [emptyMetric()]),
          remedial: data.remedial || { faultsFound: '', repairsRecommended: '', quoteRequired: false },
          notes: data.notes || '',
          serviceDate: data.serviceDate || new Date().toISOString(),
        });
      } catch (err) {
        if (!cancel) setError(formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  // ---------- mutators ----------
  const updateField = (section, key, value) =>
    setSheet((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));

  const updateChecklistItem = (idx, patch) =>
    setSheet((s) => ({
      ...s,
      checklist: s.checklist.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));

  const updateMetric = (idx, patch) =>
    setSheet((s) => ({
      ...s,
      technicalMetrics: s.technicalMetrics.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    }));
  const addMetricRow = () =>
    setSheet((s) => ({ ...s, technicalMetrics: [...s.technicalMetrics, emptyMetric()] }));
  const removeMetricRow = (idx) =>
    setSheet((s) => ({
      ...s,
      technicalMetrics: s.technicalMetrics.length <= 1
        ? s.technicalMetrics
        : s.technicalMetrics.filter((_, i) => i !== idx),
    }));

  const updateRemedial = (key, value) =>
    setSheet((s) => ({ ...s, remedial: { ...s.remedial, [key]: value } }));

  // ---------- backend calls ----------
  const ensureSheet = async () => {
    if (sheetId) return sheetId;
    const { data } = await adminAxios.post('/service-sheets', sheet);
    setSheetId(data.id);
    setServerDoc(data);
    // Replace URL silently so refresh resumes the same draft
    window.history.replaceState(null, '', `/admin/service-sheet/${data.id}`);
    return data.id;
  };

  const handleSaveDraft = async () => {
    setError('');
    setSuccess('');
    setSavingDraft(true);
    try {
      const id = sheetId || (await ensureSheet());
      if (sheetId) {
        const { data } = await adminAxios.put(`/service-sheets/${id}`, sheet);
        setServerDoc(data);
      }
      setSuccess('Draft saved');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to save');
    } finally {
      setSavingDraft(false);
      setTimeout(() => setSuccess(''), 2500);
    }
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    setError('');
    let id;
    try {
      id = sheetId || (await ensureSheet());
    } catch (err) {
      setError('Could not create sheet before uploading.');
      return;
    }
    const currentCount = serverDoc?.photos?.length || 0;
    const remaining = MAX_PHOTOS - currentCount;
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) {
      setError(`Maximum ${MAX_PHOTOS} photos per sheet`);
      return;
    }

    setUploadingCount(list.length);
    // Upload sequentially to keep mobile memory low and show progress per photo.
    for (const file of list) {
      try {
        const form = new FormData();
        form.append('file', file);
        await adminAxios.post(`/service-sheets/${id}/photos`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || 'Photo upload failed');
        break;
      } finally {
        setUploadingCount((c) => Math.max(0, c - 1));
      }
    }
    // Refresh server doc to get updated photo list
    try {
      const { data } = await adminAxios.get(`/service-sheets/${id}`);
      setServerDoc(data);
    } catch {}
  };

  const handleDeletePhoto = async (filename) => {
    if (!sheetId) return;
    try {
      await adminAxios.delete(`/service-sheets/${sheetId}/photos/${filename}`);
      const { data } = await adminAxios.get(`/service-sheets/${sheetId}`);
      setServerDoc(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSignatureSave = async (role, ref) => {
    const dataUrl = ref.current?.getDataUrl();
    if (!dataUrl) {
      setError(`Please capture the ${role} signature first.`);
      return false;
    }
    try {
      const id = sheetId || (await ensureSheet());
      await adminAxios.post(`/service-sheets/${id}/signature`, { role, dataUrl });
      const { data } = await adminAxios.get(`/service-sheets/${id}`);
      setServerDoc(data);
      return true;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Signature upload failed');
      return false;
    }
  };

  const handleFinalize = async () => {
    setError('');
    setSuccess('');

    // Persist text + engineer signature first.
    try {
      const id = sheetId || (await ensureSheet());
      if (sheetId) await adminAxios.put(`/service-sheets/${id}`, sheet);

      // Engineer signature is mandatory.
      const engineerHasNew = engineerSigRef.current && !engineerSigRef.current.isEmpty();
      const engineerOnServer = !!serverDoc?.engineerSignature;
      if (engineerHasNew) {
        const ok = await handleSignatureSave('engineer', engineerSigRef);
        if (!ok) return;
      } else if (!engineerOnServer) {
        setError('Engineer signature is required to finalize.');
        return;
      }
      // Client signature optional but encouraged
      if (clientSigRef.current && !clientSigRef.current.isEmpty()) {
        await handleSignatureSave('client', clientSigRef);
      }

      setFinalizing(true);
      const { data } = await adminAxios.post(`/service-sheets/${id}/finalize`);
      setServerDoc(data);
      setSuccess('Sheet finalized — PDF ready below.');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Finalize failed');
    } finally {
      setFinalizing(false);
    }
  };

  const pdfUrl = useMemo(() => {
    if (!sheetId || !isFinalized) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/service-sheets/${sheetId}/pdf`;
  }, [sheetId, isFinalized]);

  const handleShare = async () => {
    if (!pdfUrl) return;
    const shareData = {
      title: serverDoc?.sheetNumber || 'Service Sheet',
      text: `Service sign-off ${serverDoc?.sheetNumber} from Deep Control.`,
      url: pdfUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {}
    } else {
      // Fallback: WhatsApp web URL
      const msg = encodeURIComponent(`${shareData.text} ${pdfUrl}`);
      window.open(`https://wa.me/?text=${msg}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1829] text-slate-400" data-testid="sheet-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading sheet…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1829] text-white pb-40">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0d1829]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="text-slate-300 hover:text-white inline-flex items-center text-sm"
            data-testid="sheet-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-slate-400">Service sign-off</div>
            <div className="font-mono text-sm text-blue-300" data-testid="sheet-ref">
              {serverDoc?.sheetNumber || 'New sheet'}
            </div>
          </div>
          {isFinalized ? (
            <span className="inline-flex items-center text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full">
              <Lock className="w-3 h-3 mr-1" /> Locked
            </span>
          ) : (
            <span className="inline-flex items-center text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-full">
              Draft
            </span>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="sheet-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid="sheet-success">
            <CheckCircle2 className="w-4 h-4" /> {success}
          </div>
        )}

        {/* Engineer */}
        <SectionCard title="Engineer" testId="section-engineer">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Name" htmlFor="eng-name">
              <Input id="eng-name" disabled={isFinalized} value={sheet.engineer.name}
                     onChange={(e) => updateField('engineer', 'name', e.target.value)}
                     className="bg-[#0d1829] border-white/10 text-white" data-testid="engineer-name-input"/>
            </Field>
            <Field label="Email" htmlFor="eng-email">
              <Input id="eng-email" disabled={isFinalized} value={sheet.engineer.email}
                     onChange={(e) => updateField('engineer', 'email', e.target.value)}
                     className="bg-[#0d1829] border-white/10 text-white" data-testid="engineer-email-input"/>
            </Field>
            <Field label="Phone" htmlFor="eng-phone">
              <Input id="eng-phone" disabled={isFinalized} value={sheet.engineer.phone}
                     onChange={(e) => updateField('engineer', 'phone', e.target.value)}
                     className="bg-[#0d1829] border-white/10 text-white" data-testid="engineer-phone-input"/>
            </Field>
          </div>
        </SectionCard>

        {/* Client */}
        <SectionCard title="Client & Site" testId="section-client">
          <div className="mb-4">
            <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Property type</Label>
            <div className="flex gap-2" role="radiogroup">
              {[
                { key: 'domestic', label: 'Domestic' },
                { key: 'commercial', label: 'Commercial' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  disabled={isFinalized}
                  onClick={() => updateField('client', 'propertyType', opt.key)}
                  className={`flex-1 sm:flex-none sm:px-6 py-2 rounded-lg border text-sm font-semibold transition ${
                    sheet.client.propertyType === opt.key
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'border-white/10 text-slate-300 hover:border-white/30'
                  }`}
                  data-testid={`property-type-${opt.key}-button`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Client / Company" htmlFor="cli-name">
              <Input id="cli-name" disabled={isFinalized} value={sheet.client.name}
                     onChange={(e) => updateField('client', 'name', e.target.value)}
                     className="bg-[#0d1829] border-white/10 text-white" data-testid="client-name-input"/>
            </Field>
            <Field label="Site contact" htmlFor="cli-contact">
              <Input id="cli-contact" disabled={isFinalized} value={sheet.client.contact}
                     onChange={(e) => updateField('client', 'contact', e.target.value)}
                     className="bg-[#0d1829] border-white/10 text-white" data-testid="client-contact-input"/>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Site address" htmlFor="cli-addr">
                <Textarea id="cli-addr" disabled={isFinalized} rows={2} value={sheet.client.address}
                          onChange={(e) => updateField('client', 'address', e.target.value)}
                          className="bg-[#0d1829] border-white/10 text-white" data-testid="client-address-input"/>
              </Field>
            </div>
          </div>
        </SectionCard>

        {/* Equipment */}
        <SectionCard title="Equipment" testId="section-equipment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ['type', 'Type / System'],
              ['model', 'Make / Model'],
              ['serial', 'Serial number'],
              ['location', 'Location on site'],
              ['refrigerant', 'Refrigerant'],
            ].map(([key, label]) => (
              <Field key={key} label={label} htmlFor={`eq-${key}`}>
                <Input id={`eq-${key}`} disabled={isFinalized} value={sheet.equipment[key]}
                       onChange={(e) => updateField('equipment', key, e.target.value)}
                       className="bg-[#0d1829] border-white/10 text-white"
                       data-testid={`equipment-${key}-input`}/>
              </Field>
            ))}
          </div>
        </SectionCard>

        {/* Checklist */}
        <SectionCard title="Service Checklist" testId="section-checklist">
          <div className="space-y-3">
            {sheet.checklist.map((item, idx) => (
              <div key={idx} className="bg-[#0d1829] border border-white/10 rounded-lg p-3" data-testid={`checklist-item-${idx}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      disabled={isFinalized}
                      value={item.label}
                      onChange={(e) => updateChecklistItem(idx, { label: e.target.value })}
                      className="w-full bg-transparent text-white text-sm border-0 px-0 focus:outline-none focus:ring-0"
                      data-testid={`checklist-label-${idx}`}
                    />
                    <input
                      type="text"
                      disabled={isFinalized}
                      placeholder="Notes (optional)"
                      value={item.notes}
                      onChange={(e) => updateChecklistItem(idx, { notes: e.target.value })}
                      className="w-full bg-transparent text-slate-400 text-xs border-0 px-0 mt-1 focus:outline-none placeholder-slate-600"
                      data-testid={`checklist-notes-${idx}`}
                    />
                  </div>
                  <div className="flex gap-1.5" role="radiogroup">
                    {STATUS_LABELS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        disabled={isFinalized}
                        onClick={() => updateChecklistItem(idx, { status: opt.key })}
                        className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md border transition ${
                          item.status === opt.key
                            ? opt.color
                            : 'border-white/10 text-slate-400 hover:text-white hover:border-white/30'
                        }`}
                        data-testid={`checklist-${idx}-${opt.key.replace('/', '')}-button`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Notes */}
        <SectionCard title="Engineer Notes" testId="section-notes">
          <Textarea
            disabled={isFinalized}
            rows={4}
            value={sheet.notes}
            onChange={(e) => setSheet((s) => ({ ...s, notes: e.target.value }))}
            placeholder="Observations, recommendations, follow-up actions…"
            className="bg-[#0d1829] border-white/10 text-white"
            data-testid="sheet-notes-input"
          />
        </SectionCard>

        {/* Technical Metrics */}
        <SectionCard title="Asset Register & Technical Metrics" testId="section-metrics">
          <p className="text-xs text-slate-500 mb-4">
            Capture per-unit data. Cooling drop / heating lift is auto-calculated on the PDF from return/supply temperatures.
          </p>
          <div className="space-y-3">
            {sheet.technicalMetrics.map((m, idx) => {
              let diff = null;
              const r = parseFloat(m.returnAir);
              const s = parseFloat(m.supplyAir);
              if (!Number.isNaN(r) && !Number.isNaN(s)) diff = s - r;
              return (
                <div key={idx} className="bg-[#0d1829] border border-white/10 rounded-lg p-3 space-y-2" data-testid={`metric-row-${idx}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Input disabled={isFinalized} placeholder="Unit ID" value={m.unitId}
                           onChange={(e) => updateMetric(idx, { unitId: e.target.value })}
                           className="bg-[#1e2d4a] border-white/10 text-white text-sm" data-testid={`metric-${idx}-unitId`}/>
                    <Input disabled={isFinalized} placeholder="Location" value={m.location}
                           onChange={(e) => updateMetric(idx, { location: e.target.value })}
                           className="bg-[#1e2d4a] border-white/10 text-white text-sm" data-testid={`metric-${idx}-location`}/>
                    <Input disabled={isFinalized} placeholder="Gas (e.g. R32)" value={m.gasType}
                           onChange={(e) => updateMetric(idx, { gasType: e.target.value })}
                           className="bg-[#1e2d4a] border-white/10 text-white text-sm" data-testid={`metric-${idx}-gasType`}/>
                    <Input disabled={isFinalized} placeholder="Charge (kg)" value={m.charge}
                           onChange={(e) => updateMetric(idx, { charge: e.target.value })}
                           className="bg-[#1e2d4a] border-white/10 text-white text-sm" data-testid={`metric-${idx}-charge`}/>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
                    <Input disabled={isFinalized} type="number" step="0.1" placeholder="Return °C" value={m.returnAir}
                           onChange={(e) => updateMetric(idx, { returnAir: e.target.value })}
                           className="bg-[#1e2d4a] border-white/10 text-white text-sm" data-testid={`metric-${idx}-returnAir`}/>
                    <Input disabled={isFinalized} type="number" step="0.1" placeholder="Supply °C" value={m.supplyAir}
                           onChange={(e) => updateMetric(idx, { supplyAir: e.target.value })}
                           className="bg-[#1e2d4a] border-white/10 text-white text-sm" data-testid={`metric-${idx}-supplyAir`}/>
                    <select
                      disabled={isFinalized}
                      value={m.leakStatus}
                      onChange={(e) => updateMetric(idx, { leakStatus: e.target.value })}
                      className="bg-[#1e2d4a] border border-white/10 text-white text-sm rounded-md px-2 py-2"
                      data-testid={`metric-${idx}-leakStatus`}
                    >
                      <option>F-Gas Leak — Pass</option>
                      <option>F-Gas Leak — Fail</option>
                      <option>N/A</option>
                    </select>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      {diff !== null ? (
                        <span className={`font-mono ${diff < 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          Δ {diff.toFixed(1)} °C
                        </span>
                      ) : (
                        <span className="text-slate-600">Δ —</span>
                      )}
                      {!isFinalized && sheet.technicalMetrics.length > 1 && (
                        <button
                          onClick={() => removeMetricRow(idx)}
                          className="text-slate-400 hover:text-red-400 text-xs"
                          data-testid={`metric-${idx}-remove`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {!isFinalized && (
              <button
                type="button"
                onClick={addMetricRow}
                className="w-full text-sm text-blue-300 hover:text-blue-200 border border-dashed border-white/15 hover:border-blue-400 rounded-lg py-2"
                data-testid="metric-add-row"
              >
                + Add another unit
              </button>
            )}
          </div>
        </SectionCard>

        {/* Recommended Remedial Works */}
        <SectionCard title="Recommended Remedial Works" testId="section-remedial">
          <p className="text-xs text-slate-500 mb-4">
            Turns a maintenance visit into a quotable repair pipeline.
          </p>
          <div className="space-y-4">
            <Field label="Faults found">
              <Textarea
                disabled={isFinalized}
                rows={3}
                placeholder="e.g. Indoor unit blower bearing noisy, condensate tray cracked..."
                value={sheet.remedial.faultsFound}
                onChange={(e) => updateRemedial('faultsFound', e.target.value)}
                className="bg-[#0d1829] border-white/10 text-white"
                data-testid="remedial-faults-input"
              />
            </Field>
            <Field label="Repairs recommended">
              <Textarea
                disabled={isFinalized}
                rows={3}
                placeholder="e.g. Replace blower assembly, fit new drain tray, perform leak test..."
                value={sheet.remedial.repairsRecommended}
                onChange={(e) => updateRemedial('repairsRecommended', e.target.value)}
                className="bg-[#0d1829] border-white/10 text-white"
                data-testid="remedial-repairs-input"
              />
            </Field>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">
                Quote required?
              </Label>
              <div className="flex gap-2" role="radiogroup">
                {[
                  { key: true, label: 'Yes', activeClass: 'bg-red-500 border-red-500 text-white' },
                  { key: false, label: 'No', activeClass: 'bg-emerald-500 border-emerald-500 text-white' },
                ].map((opt) => (
                  <button
                    key={String(opt.key)}
                    type="button"
                    disabled={isFinalized}
                    onClick={() => updateRemedial('quoteRequired', opt.key)}
                    className={`flex-1 sm:flex-none sm:px-8 py-2 rounded-lg border text-sm font-semibold transition ${
                      sheet.remedial.quoteRequired === opt.key
                        ? opt.activeClass
                        : 'border-white/10 text-slate-300 hover:border-white/30'
                    }`}
                    data-testid={`remedial-quote-${opt.label.toLowerCase()}-button`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Photos */}
        <SectionCard title={`Service Photos (${serverDoc?.photos?.length || 0}/${MAX_PHOTOS})`} testId="section-photos">
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {(serverDoc?.photos || []).map((p) => (
              <div key={p.filename} className="relative aspect-square rounded-lg overflow-hidden bg-[#0d1829] border border-white/10">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/service-sheets/${sheetId}/photos/${p.filename}`}
                  alt={p.caption || 'service photo'}
                  className="w-full h-full object-cover"
                  data-testid={`sheet-photo-${p.filename}`}
                />
                {!isFinalized && (
                  <button
                    onClick={() => handleDeletePhoto(p.filename)}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 rounded-full p-1 text-white"
                    data-testid={`sheet-photo-delete-${p.filename}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {!isFinalized && (serverDoc?.photos?.length || 0) < MAX_PHOTOS && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-lg border-2 border-dashed border-white/15 hover:border-blue-400 hover:bg-blue-500/5 transition flex flex-col items-center justify-center text-slate-400 text-xs gap-1"
                data-testid="sheet-add-photo-button"
              >
                {uploadingCount > 0 ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Uploading…</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5" />
                    <span>Add photo</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => {
              handlePhotoUpload(e.target.files);
              e.target.value = '';
            }}
            data-testid="sheet-photo-file-input"
          />
          <p className="text-xs text-slate-500 mt-3">
            Photos are resized server-side to ~1600px so your phone never freezes uploading high-res shots.
          </p>
        </SectionCard>

        {/* Signatures */}
        <SectionCard title="Sign-Off" testId="section-signatures">
          {isFinalized ? (
            <div className="text-sm text-slate-300">
              <Lock className="w-4 h-4 inline mr-1.5 text-emerald-400" />
              This sheet is finalized and the signatures are locked.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <SignaturePad ref={engineerSigRef} label="Engineer signature" testId="engineer-signature" />
                <p className="text-xs text-slate-500 mt-1">Signed: {sheet.engineer.name || '—'}</p>
              </div>
              <div>
                <SignaturePad ref={clientSigRef} label="Client signature (optional)" testId="client-signature" />
                <p className="text-xs text-slate-500 mt-1">Signed: {sheet.client.contact || sheet.client.name || '—'}</p>
              </div>
            </div>
          )}
        </SectionCard>

        {/* PDF + Share (only when finalized) */}
        {isFinalized && pdfUrl && (
          <SectionCard title="PDF & Share" testId="section-share">
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0d1829] border border-white/10 hover:border-blue-400 text-white rounded-lg px-4 py-3"
                data-testid="sheet-view-pdf-link"
              >
                <FileText className="w-4 h-4" /> Open PDF
              </a>
              <Button
                onClick={handleShare}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="sheet-share-button"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share via WhatsApp / system
              </Button>
            </div>
          </SectionCard>
        )}
      </main>

      {/* Sticky bottom actions */}
      {!isFinalized && (
        <div className="fixed bottom-0 inset-x-0 bg-[#0d1829]/95 backdrop-blur border-t border-white/10 px-4 sm:px-6 py-3 z-20">
          <div className="max-w-3xl mx-auto flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSaveDraft}
              disabled={savingDraft || finalizing}
              className="flex-1 text-slate-200 border border-white/15 hover:bg-white/5"
              data-testid="sheet-save-draft-button"
            >
              {savingDraft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save draft
            </Button>
            <Button
              onClick={handleFinalize}
              disabled={savingDraft || finalizing}
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="sheet-finalize-button"
            >
              {finalizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Finalize & generate PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
